/**
 * ──────────────────────────────────────────────────────────────────────────
 *  CONTEXT-LOCKED PALETTE — Utility Outage family (Ice Storm Risk →
 *  "Power Outages"; operator-cop-doctrine §3A.7 context-locking / §3B.8 the
 *  AMBIENT register / §4.5 ice-storm impact picture).
 *
 *  This module is the SINGLE source of the utility-outage palette. It is consumed
 *  ONLY by the power-outages family:
 *    - PowerOutagesLayer map markers (the ambient status-coloured dot) + the
 *      ground-clamped service-area polygon fill
 *    - the UtilityOutageBody side-panel status badge + legend
 *
 *  Per §3B.8 (founder revision 2026-06-05): on the MAP, SHAPE encodes "an outage"
 *  (an ambient affected-AREA — published polygon or approximate disc — under a
 *  fixed round dot; never a flame, never a pulsing anomaly dot); SIZE encodes
 *  magnitude (customers-affected band); COLOR is the outage red (Colour Law 2 (b):
 *  dot OUTAGE_MAP_DOT_RED #c25b52 over area OUTAGE_MAP_AREA_RED #8f3530) for every
 *  non-restored outage — restored outages leave the map; MOTION is NOT in this
 *  family's budget (outages are ambient, never pulse).
 *  Crew-dispatched vs active is read from the PANEL badge (the per-status palette
 *  below) + a small map glyph, not from the dot colour.
 *
 *  Status hues — now the PANEL-ONLY register (side-panel status badge + legend):
 *    active           muted red       #e0564d   (power out, ongoing)
 *    crew_dispatched  muted amber     #e0a458   (crew en route / on site)
 *    restored         muted green     #5fae7e   (power back — review only)
 *    unknown          neutral grey    #9ca3af   (unmapped upstream status)
 *
 *  Overlap debt this lock acknowledges (§3A.7): the map's outage reds (dot
 *  #c25b52 / area #8f3530) are DISTINCT shades from the anomaly map red and the
 *  flame red; the panel's muted hexes are desaturated cousins of other families'
 *  vivid hexes. This module
 *  owns its OWN hexes and must never import a sibling palette. Shape (an ambient
 *  outage area, not a flame or pulsing anomaly dot) keeps the families
 *  distinguishable in monochrome.
 *
 *  ⛔ Do NOT import from ./responseLevel / ./fireStages / ./capHazards /
 *     ./communityStatus / ./rawHotspots / ./lifecycleStates / ./sectorStyles
 *     (enforced by the no-restricted-imports lint rule in eslint.config.js).
 * ──────────────────────────────────────────────────────────────────────────
 */

/**
 * FOUNDER MARKER-DOCTRINE REVISION (2026-06-05, §3B.8): on the MAP every
 * non-restored outage renders RED (dot OUTAGE_MAP_DOT_RED over area
 * OUTAGE_MAP_AREA_RED, Colour Law 2 (b)) — the map drops the per-status
 * hue. Crew-dispatched vs active is read from the panel badge + a small map glyph,
 * not colour; RESTORED outages leave the map entirely. The per-status palette
 * below is now the PANEL-ONLY register (the side-panel status badge + legend),
 * mirroring how the Response-Level palette survives in the queue/badge while the
 * anomaly map dots go red.
 */

/** The MAP reds for every non-restored outage — Colour Law 2 (b) splits the
 *  outage red into a brighter DOT and a darker AREA fill so the dot reads as the
 *  click target above its own faint footprint, and the luminance gap (dot ≈3.4:1
 *  vs URGENT-RED, area ≈3.6:1 vs the OC-FUL flame on the dark globe) survives
 *  deuteranopia. Both are DISTINCT shades from the anomaly map red (#ff3b30,
 *  responseLevel ANOMALY_MAP_RED) and the flame red (#e25822, fireStages OC-FUL)
 *  so the reds read apart at a glance (Law 1/2 + §3A.7 distinct-shades law); SHAPE
 *  (an ambient area + sized static dot, never a flame or pulsing anomaly dot)
 *  keeps them distinguishable in monochrome too. */
export const OUTAGE_MAP_DOT_RED = '#c25b52';
export const OUTAGE_MAP_AREA_RED = '#8f3530';

/** The per-status muted hexes — the PANEL status badge palette (see note above). */
export const OUTAGE_STATUS_COLORS: Record<string, string> = {
  active: '#e0564d', // muted red — power out
  crew_dispatched: '#e0a458', // muted amber — crew en route / on site
  restored: '#5fae7e', // muted green — power back
};

/** Neutral grey for an unknown / unmapped status (never dropped). */
export const OUTAGE_DEFAULT_COLOR = '#9ca3af';

/** Short human label per status, paired with the colour so hue is never alone. */
export const OUTAGE_STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  crew_dispatched: 'Crew dispatched',
  restored: 'Restored',
  unknown: 'Unknown',
};

export interface OutageStyle {
  /** CSS hex for the dot fill + polygon fill + badge. */
  color: string;
  /** Status label — colour is never load-bearing alone. */
  label: string;
}

/**
 * Resolve the render style for an outage's status. Tolerant of null / unknown
 * (→ the neutral grey style + an "Unknown" label, never dropped).
 */
export function outageStyle(status: string | null | undefined): OutageStyle {
  const key = status ?? 'unknown';
  return {
    color: OUTAGE_STATUS_COLORS[key] ?? OUTAGE_DEFAULT_COLOR,
    label: OUTAGE_STATUS_LABEL[key] ?? (status || 'Unknown'),
  };
}

/**
 * Customers-affected → marker diameter band (px). SIZE encodes magnitude (§3B.8).
 * The smallest band (6px) is below the L1 anomaly dot (10px) so a tiny outage
 * never competes with a scored Anomaly-Queue dot; the largest (22px) signals a
 * mass outage at a glance. NULL / 0 (a new, unconfirmed or masked-count outage)
 * uses the smallest band but is never dropped.
 */
export const OUTAGE_DOT_BANDS: ReadonlyArray<{ min: number; size: number; label: string }> = [
  { min: 1000, size: 22, label: '1,000+ customers' },
  { min: 250, size: 17, label: '250–999 customers' },
  { min: 50, size: 13, label: '50–249 customers' },
  { min: 1, size: 9, label: '1–49 customers' },
  { min: 0, size: 6, label: 'Unconfirmed / few' },
];

/** Fill opacity for the ambient dot — solid enough to read, quiet register. */
export const OUTAGE_DOT_ALPHA = 0.85;
/** Fill opacity for the ground-clamped, PUBLISHED service-area polygon — faint,
 *  under the dot. */
export const OUTAGE_AREA_ALPHA = 0.18;
/** Fill opacity for an APPROXIMATE affected-area disc (no published polygon).
 *  Fainter than a real footprint so the two read apart; the disc also carries an
 *  outline ring (PowerOutagesLayer) the clamped real polygon lacks. */
export const OUTAGE_APPROX_AREA_ALPHA = 0.1;

/** The marker diameter (px) for an affected-customer count (null → smallest band). */
export function outageDotSize(customersAffected: number | null | undefined): number {
  const n = customersAffected ?? 0;
  for (const band of OUTAGE_DOT_BANDS) {
    if (n >= band.min) return band.size;
  }
  return OUTAGE_DOT_BANDS[OUTAGE_DOT_BANDS.length - 1]!.size;
}

/**
 * Radius (metres) of the APPROXIMATE affected-area disc when an outage has no
 * published service-area polygon (founder revision 2026-06-05, §3B.8 — "every
 * outage reads as an area"). Scales with the customers-affected band so a mass
 * outage reads as a bigger zone; null/0 → the smallest disc, never dropped.
 *
 * #389 VERIFY (2026-06-20): the "outage pulses + oversized blue zone" report is a
 * MISATTRIBUTION, not an outage bug. (1) Outages render STATIC red — dot
 * OUTAGE_MAP_DOT_RED #c25b52 over area OUTAGE_MAP_AREA_RED #8f3530, never animated
 * (PowerOutagesLayer adds no CallbackProperty); the "pulse" the operator saw was a
 * CO-LOCATED anomaly dot stacked on the outage (now separable via the #390 click-
 * cycle, and spread apart by the #393 seed fix). (2) The "blue zone" is NOT an
 * outage — outages are red; blue belongs to a CAP / flood / ice / comms-degradation
 * polygon. (3) These radii (2/4/7/12 km) are a deliberate magnitude ramp for the
 * NO-PUBLISHED-FOOTPRINT fallback disc — metres, correct units, no off-by-1000 bug;
 * a 12 km disc for a 1,000+ customer mass outage is large but defensible for a rural
 * LDC. Resizing the bands is a visual/product call (not a clear code bug), so it is
 * LEFT as-is pending founder direction rather than changed unprompted.
 */
export const OUTAGE_APPROX_RADIUS_BANDS: ReadonlyArray<{ min: number; radiusM: number }> = [
  { min: 1000, radiusM: 12000 },
  { min: 250, radiusM: 7000 },
  { min: 50, radiusM: 4000 },
  { min: 1, radiusM: 2000 },
  { min: 0, radiusM: 1200 },
];

export function outageApproxRadiusM(customersAffected: number | null | undefined): number {
  const n = customersAffected ?? 0;
  for (const band of OUTAGE_APPROX_RADIUS_BANDS) {
    if (n >= band.min) return band.radiusM;
  }
  return OUTAGE_APPROX_RADIUS_BANDS[OUTAGE_APPROX_RADIUS_BANDS.length - 1]!.radiusM;
}
